import React, { createContext, useContext, useReducer } from "react";

// Estados del tour
const TOUR_ACTIONS = {
  START_TOUR: "START_TOUR",
  END_TOUR: "END_TOUR",
  SET_CURRENT_STEP: "SET_CURRENT_STEP",
  SET_CURRENT_PAGE: "SET_CURRENT_PAGE",
  SET_CONTINUE_TOUR: "SET_CONTINUE_TOUR",
  COMPLETE_STEP: "COMPLETE_STEP",
  RESET_TOUR: "RESET_TOUR",
  SET_MANUAL_ACTIVE: "SET_MANUAL_ACTIVE",
  SET_STEP3_ACTIVE: "SET_STEP3_ACTIVE",
};

// Estado inicial del tour
const initialState = {
  isActive: false,
  currentPage: null,
  currentStep: 0,
  continueTour: false,
  completedSteps: [],
  totalSteps: 6, // Basado en la imagen que enviaste
  isActiveManual: false,
  isStep3Active: false, // Nueva variable para el paso 3
};

// Reducer para manejar el estado del tour
const tourReducer = (state, action) => {
  switch (action.type) {
    case TOUR_ACTIONS.SET_MANUAL_ACTIVE:
      return {
        ...state,
        isActiveManual: action.payload,
      };
    case TOUR_ACTIONS.SET_STEP3_ACTIVE:
      return {
        ...state,
        isStep3Active: action.payload,
      };
    case TOUR_ACTIONS.START_TOUR:
      return {
        ...state,
        isActive: true,
        currentPage: action.payload?.page || state.currentPage,
      };

    case TOUR_ACTIONS.END_TOUR:
      return {
        ...state,
        isActive: false,
        currentPage: null,
        continueTour: false,
      };

    case TOUR_ACTIONS.SET_CURRENT_STEP:
      return {
        ...state,
        currentStep: action.payload,
      };

    case TOUR_ACTIONS.SET_CURRENT_PAGE:
      return {
        ...state,
        currentPage: action.payload,
      };

    case TOUR_ACTIONS.SET_CONTINUE_TOUR:
      return {
        ...state,
        continueTour: action.payload,
      };

    case TOUR_ACTIONS.COMPLETE_STEP:
      const stepKey = action.payload;
      if (!state.completedSteps.includes(stepKey)) {
        return {
          ...state,
          completedSteps: [...state.completedSteps, stepKey],
        };
      }
      return state;

    case TOUR_ACTIONS.RESET_TOUR:
      return {
        ...initialState,
        isActiveManual: false,
        isStep3Active: false,
      };

    default:
      return state;
  }
};

// Crear el contexto
const TourContext = createContext();

// Provider del contexto
export const TourProvider = ({ children }) => {
  const [state, dispatch] = useReducer(tourReducer, initialState);

  // Actions
  const startTour = (page = null) => {
    dispatch({ type: TOUR_ACTIONS.START_TOUR, payload: { page } });
  };

  const endTour = () => {
    dispatch({ type: TOUR_ACTIONS.END_TOUR });
  };

  const setCurrentStep = (step) => {
    dispatch({ type: TOUR_ACTIONS.SET_CURRENT_STEP, payload: step });
  };

  const setCurrentPage = (page) => {
    dispatch({ type: TOUR_ACTIONS.SET_CURRENT_PAGE, payload: page });
  };

  const setContinueTour = (value) => {
    dispatch({ type: TOUR_ACTIONS.SET_CONTINUE_TOUR, payload: value });
  };

  const completeStep = (stepKey) => {
    dispatch({ type: TOUR_ACTIONS.COMPLETE_STEP, payload: stepKey });
  };

  const resetTour = () => {
    dispatch({ type: TOUR_ACTIONS.RESET_TOUR });
  };

  const setManualActive = (value) => {
    dispatch({ type: TOUR_ACTIONS.SET_MANUAL_ACTIVE, payload: value });
  };

  const setStep3Active = (value) => {
    dispatch({ type: TOUR_ACTIONS.SET_STEP3_ACTIVE, payload: value });
  };

  const value = {
    ...state,
    startTour,
    endTour,
    setCurrentStep,
    setCurrentPage,
    setContinueTour,
    completeStep,
    resetTour,
    setManualActive,
    setStep3Active,
  };

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};

// Hook para usar el contexto
export const useTourContext = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error("useTourContext debe usarse dentro de TourProvider");
  }
  return context;
};

export { TOUR_ACTIONS };
